import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef
} from 'react'
import {
  Button,
  Input,
  message,
  Drawer,
  Tabs,
  Modal,
  Spin,
  Icon,
  Dropdown,
  Menu,
  Progress
} from 'antd'
import './VideoGeneration.scss'

import StepTitle from '../StepTitle'
import { SettingsContent, AdvancedSettingsContent } from './components/Settings'
import Scene from './components/Scene'
import {
  changeMachineState,
  assembleScenesWithAudio,
  generateTransition,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import ReactPlayer from 'react-player'
import AIVideoModal from './components/FinalVideo'
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove
} from 'react-sortable-hoc'
import VideoEditorModal from './components/VideoEditor'
import { LeftOutlined } from '@ant-design/icons'
import GenerationStatusModal from './components/GenerationStatusModal'
import RegenerationInfoModal from './components/RegenerationInfoModal'
import { useCalculateDurations } from '../../hooks/useCalculateDurations'
import StatusView from '../StatusView/StatusView'

const VideoGeneration = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onGoBack,
    videoGenerating,
    generatingVideoForAllScenes,
    handleRegenerateVideo,
    handleRetryFailedVideos,
    handleGenerateFullVideo,
    updateShotDataRef,
    videoProvider,
    setScenes
  } = props

  // ===== MAIN STATE =====
  const scenes = formData.shotList || []
  const [transitions, setTransitions] = useState({})

  // ===== SCENE PREVIEW CACHE STATE =====
  const [scenePreviewCache, setScenePreviewCache] = useState({})
  // Structure: { sceneId: { videoUrl: 'url', shotVersions: { shotId: vidSrc } } }

  // ===== SETTINGS STATE =====
  const [editingSettings, setEditingSettings] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [advancedSettings, setAdvancedSettings] = useState(null)
  const [advancedSettingsVisible, setAdvancedSettingsVisible] = useState(false)

  // ===== MODAL STATE =====

  const [showVideoEditor, setShowVideoEditor] = useState(false)
  const [generateMainVideo, setGenerateMainVideo] = useState(false)
  const [showMainVideo, setShowMainVideo] = useState(false)
  const [finalVideoError, setFinalVideoError] = useState(null)

  // ===== VIDEO EDITOR STATE =====
  const [editorScenes, setEditorScenes] = useState([])
  const [editorTransitions, setEditorTransitions] = useState({})

  // ===== SCENE PREVIEW STATE =====
  const [showScenePreview, setShowScenePreview] = useState(false)
  const [previewSceneVideo, setPreviewSceneVideo] = useState(null)
  const [scenePreviewError, setScenePreviewError] = useState(null)
  const [showRegenerationInfo, setShowRegenerationInfo] = useState(false)
  const [affectedScenesByMismatch, setAffectedScenesByMismatch] = useState([])
  const autoRegenerationTriggeredRef = useRef(false)

  // ===== HELPER FUNCTIONS =====
  const generateId = (prefix = '') =>
    `${prefix}${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 8)}`

  // ===== DURATION CALCULATION HOOK =====
  const {
    calculate: callCalculateDurations,
    isCalculating: isCalculatingDurations,
    progress: calculationProgress
  } = useCalculateDurations()

  // Replace __character1__, __character2__ etc. in prompt with selected character names
  const getFormattedPrompt = prompt => {
    const selectedCharacters = formData.selectedCharacter || []
    const formattedPrompt = selectedCharacters.reduce((acc, char, index) => {
      const placeholder = `__character${index + 1}__`
      return acc.replace(
        new RegExp(placeholder, 'g'),
        char.current.replace(/__/g, '')
      )
    }, prompt)
    return formattedPrompt
  }

  const formatDurationOutput = (output, existingScenes) => {
    return Object.entries(output).map(([sceneKey, sceneValue], index) => {
      const existingScene = existingScenes.find(s => s.id === sceneKey) || {}

      const { scene_location = '', shots = [] } = sceneValue

      // Format individual shots within the scene
      const formattedShots = shots.map((shotObj, shotIndex) => {
        const shotKey = Object.keys(shotObj)[0]
        const shotValue = shotObj[shotKey]

        const existingShot =
          (existingScene.shots || []).find(s => s.key === shotKey) || {}

        return {
          ...existingShot,
          id: existingShot.id || `${sceneKey}-shot-${shotIndex + 1}`,
          key: shotKey,
          title: shotValue.title || existingShot.title || '',
          initial_image_prompt:
            shotValue['initial image prompt'] ||
            shotValue.initial_image_prompt ||
            existingShot.initial_image_prompt ||
            '',
          scenery: shotValue.scenery || existingShot.scenery || '',
          shot_direction:
            shotValue['shot direction'] ||
            shotValue.shot_direction ||
            existingShot.shot_direction ||
            '',
          video_prompt:
            shotValue['video prompt'] ||
            shotValue.video_prompt ||
            existingShot.video_prompt ||
            '',
          voiceover_text:
            shotValue['voiceover text'] ||
            shotValue.voiceover_text ||
            existingShot.voiceover_text ||
            '',
          lighting: shotValue.lighting || existingShot.lighting || '',
          dialogue: shotValue.dialogue || existingShot.dialogue || {},
          video_duration:
            shotValue.video_duration || existingShot.video_duration || '',
          generated_video_duration:
            existingShot.generated_video_duration ||
            (existingShot.vidSrc ? existingShot.video_duration : null)
        }
      })

      return {
        ...existingScene,
        id: sceneKey,
        title: sceneKey,
        description: scene_location || existingScene.description,
        shots: formattedShots,
        aggregated_voiceover:
          sceneValue.aggregated_voiceover ||
          existingScene.aggregated_voiceover ||
          '',
        audio_duration:
          sceneValue.audio_duration || existingScene.audio_duration || 0,
        audio_duration_estimated:
          sceneValue.audio_duration_estimated !== undefined
            ? sceneValue.audio_duration_estimated
            : existingScene.audio_duration_estimated,
        audio_link: sceneValue.audio_link || existingScene.audio_link || '',
        scene_location: scene_location || existingScene.scene_location
      }
    })
  }

  const constructAssemblyPayload = (
    targetData = scenes,
    isFinalVideo = false
  ) => {
    let clustered_shooting_list = {}
    let voiceover_paths = {}
    let trimmed_video_paths = {}

    // Normalize input data: if it's a flat array of shots (clips), group it into scenes
    let normalizedScenes = []
    const isFlatClips =
      Array.isArray(targetData) &&
      targetData.length > 0 &&
      !targetData[0].shots &&
      targetData[0].sceneId

    if (isFlatClips) {
      // Group flat clips by sceneId while preserving order
      const scenesMap = new Map()
      targetData.forEach(clip => {
        if (!scenesMap.has(clip.sceneId)) {
          scenesMap.set(clip.sceneId, {
            id: clip.sceneId,
            title: clip.sceneTitle || clip.sceneId,
            location: clip.location || '',
            voiceover_text: clip.aggregated_voiceover || '',
            audio_link: clip.audio_link || '',
            audio_duration: clip.audio_duration || 0,
            shots: []
          })
        }
        scenesMap.get(clip.sceneId).shots.push(clip)
      })
      normalizedScenes = Array.from(scenesMap.values())
    } else {
      normalizedScenes = targetData
    }

    normalizedScenes.forEach((scene, index) => {
      const sceneKey = scene.id || `scene ${index + 1}`

      // 1. Build clustered_shooting_list
      clustered_shooting_list[sceneKey] = {
        scene_location:
          scene.location ||
          scene.scene_location ||
          `__location${index + 1}.1__`,
        aggregated_voiceover:
          scene.voiceover_text || scene.aggregated_voiceover || '',
        audio_link: scene.audio_link || '',
        audio_duration: scene.audio_duration || 0,
        audio_duration_estimated: scene.audio_duration_estimated || false,
        shots: scene.shots.map((shot, shotIndex) => {
          const shotKey = shot.key || `shot ${shotIndex + 1}`
          return {
            [shotKey]: {
              title: shot.title || shot.shotTitle || `Shot ${shotIndex + 1}`,
              shot_direction: shot.shot_direction || '',
              dialogue: shot.dialogue || {},
              voiceover_text: shot.voiceover_text || '',
              lighting: shot.lighting || '',
              location: shot.location || '',
              initial_image_prompt: shot.initial_image_prompt || '',
              video_prompt: shot.video_prompt || '',
              scenery: shot.scenery || '',
              video_duration: shot.video_duration,
              shot_number: shotIndex + 1,
              generated_image_url: shot.imageSrc || '',
              video_url: shot.vidSrc || ''
            }
          }
        })
      }

      // 2. Build voiceover_paths
      if (scene.audio_link) {
        voiceover_paths[sceneKey] = scene.audio_link
      }

      // 3. Build trimmed_video_paths
      const sceneShots = {}
      scene.shots.forEach((shot, shotIndex) => {
        const shotKey = shot.key || `shot ${shotIndex + 1}`
        if (shot.vidSrc) {
          sceneShots[shotKey] = shot.vidSrc
        }
      })
      if (Object.keys(sceneShots).length > 0) {
        trimmed_video_paths[sceneKey] = sceneShots
      }
    })

    const scene_keys = Object.keys(clustered_shooting_list).filter(
      key => key !== 'location_map'
    )
    clustered_shooting_list.location_map = formData.locationMap

    return {
      clustered_shooting_list,
      voiceover_paths,
      trimmed_video_paths,
      scene_keys: isFinalVideo ? null : scene_keys,
      s3_output_prefix: `test_output/full_assembly/`
    }
  }

  const invalidateScenePreview = sceneId => {
    setScenePreviewCache(prev => {
      const newCache = { ...prev }
      delete newCache[sceneId]
      return newCache
    })
  }

  // ===== VIDEO TASK POLLING =====
  async function waitForVideoTaskSuccess(taskId, onProgress) {
    try {
      if (!taskId) return null
      const delayMs = 10000

      while (true) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status || null

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (status === 'success') {
            return data?.output || ''
          }
          if (status === 'failed' || status === 'error') {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            // If pending but progress is 100%, retry immediately/quickly
            await new Promise(resolve => setTimeout(resolve, 1000))
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          return null
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    } catch (err) {
      return null
    }
  }

  const handleGenerateFinalVideo = async (orderedClips = null) => {
    try {
      setShowVideoEditor(false)
      setGenerateMainVideo(true)
      setFinalVideoError(null)
      setShowMainVideo(false)

      // Use orderedClips if provided, otherwise fallback to scenes
      const payload = constructAssemblyPayload(
        orderedClips && orderedClips.length > 0 ? orderedClips : scenes,
        true
      )
      const response = await assembleScenesWithAudio(payload)
      const taskId = response?.data?.task_id
      const output = await waitForVideoTaskSuccess(taskId)
      const finalVideos = output?.final_videos || {}
      const finalVideo = finalVideos.full_video || Object.values(finalVideos)[0]

      if (!finalVideo) throw new Error('Final video generation failed')

      setFormData(prev => ({ ...prev, finalVideo }))
    } catch (error) {
      console.error('Error generating final video:', error)
      message.error('Failed to generate final video: ' + error.message)
      setFinalVideoError(error?.message || 'Unknown error occurred')
    } finally {
      setGenerateMainVideo(false)
      setShowMainVideo(true)
    }
  }

  // ===== SCENE PREVIEW HANDLER =====
  const handlePreviewScene = async sceneId => {
    const selectedScene = scenes.find(s => s.id === sceneId)
    if (!selectedScene) return

    const allShotsHaveVideos = selectedScene.shots.every(shot => shot.vidSrc)
    if (!allShotsHaveVideos) {
      message.warning('Not all shots in this scene have generated videos yet.')
      return
    }

    // Check if we have a valid cached preview
    const cachedPreview = scenePreviewCache[sceneId]
    if (cachedPreview?.videoUrl && cachedPreview?.shotVersions) {
      // Verify all shots match the cached versions
      const allShotsMatch = selectedScene.shots.every(
        shot => cachedPreview.shotVersions[shot.id] === shot.vidSrc
      )

      if (allShotsMatch) {
        // Use cached preview
        setPreviewSceneVideo(cachedPreview.videoUrl)
        setShowScenePreview(true)
        message.success('Loaded cached scene preview')
        return
      }
    }

    try {
      const filtered = {}
      Object.keys(transitions || {}).forEach(key => {
        if (key.startsWith(`${sceneId}-`)) {
          filtered[key] = transitions[key]
        }
      })

      const orderedClips = selectedScene.shots.map(shot => ({
        ...shot,
        sceneId: selectedScene.id,
        sceneId: selectedScene.id,
        shotId: shot.id,
        audio_link: selectedScene.audio_link
      }))

      setShowScenePreview(true)
      setPreviewSceneVideo(null)
      setScenePreviewError(null)

      // We only want to generate preview for the selected scene
      const payload = constructAssemblyPayload([selectedScene], false)
      const response = await assembleScenesWithAudio(payload)
      const taskId = response?.data?.task_id
      const output = await waitForVideoTaskSuccess(taskId)
      const finalVideos = output?.final_videos || {}
      const finalVideo = finalVideos.full_video || Object.values(finalVideos)[0]

      if (finalVideo) {
        setPreviewSceneVideo(finalVideo)

        // Cache the preview with shot versions
        const shotVersions = {}
        selectedScene.shots.forEach(shot => {
          shotVersions[shot.id] = shot.vidSrc
        })

        setScenePreviewCache(prev => ({
          ...prev,
          [sceneId]: {
            videoUrl: finalVideo,
            shotVersions
          }
        }))

        message.success('Scene preview generated successfully!')
      } else {
        throw new Error('Scene preview generation failed')
      }
    } catch (error) {
      console.error('Error generating scene preview:', error)
      setScenePreviewError(error?.message || 'Failed to generate scene preview')
    }
  }

  // ===== SETTINGS HANDLERS =====
  const handleEditSettings = (sceneId, shotId) => {
    setEditingSettings({ sceneId, shotId })
    setDrawerVisible(true)
  }

  const handleAdvancedSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setAdvancedSettings({
      sceneId,
      shotId,
      characterId: shot.characterId || formData?.characterList?.[0]?.id || null,
      shotType: shot.shotType || 'Medium Shot',
      imageSrc: shot.imageSrc || null,
      prompt: shot.prompt || '',
      ...shot
    })
    setAdvancedSettingsVisible(true)
  }

  const updateSettings = (key, value) => {
    if (!editingSettings) return
    const { sceneId, shotId } = editingSettings
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map(shot =>
                shot.id === shotId ? { ...shot, [key]: value } : shot
              )
            }
          : scene
      )
    )
  }

  const updateAdvancedSettings = (key, value) => {
    setAdvancedSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveSettings = (updatedFields = null) => {
    Modal.confirm({
      title: 'Confirm Changes',
      content:
        'Are you sure you want to save these settings? This will regenerate the video.',
      centered: true,
      onOk: () => {
        if (updatedFields && editingSettings) {
          const { sceneId, shotId } = editingSettings
          setScenes(prevScenes =>
            prevScenes.map(scene =>
              scene.id === sceneId
                ? {
                    ...scene,
                    shots: scene.shots.map(shot =>
                      shot.id === shotId ? { ...shot, ...updatedFields } : shot
                    )
                  }
                : scene
            )
          )
        }
        setDrawerVisible(false)
        setEditingSettings(null)

        message.info('Video is regenerating...')
        const { sceneId, shotId } = editingSettings
        handleRegenerateVideo(sceneId, shotId)
      }
    })
  }

  const saveAdvancedSettings = () => {
    const { sceneId, shotId, ...updatedFields } = advancedSettings

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    ...updatedFields
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setAdvancedSettingsVisible(false)
    message.success('Advanced settings updated successfully!')
  }

  // ===== TRANSITION HANDLERS =====
  const handleTransitionSelect = (sceneId, position, transitionType) => {
    setTransitions(prev => {
      const newTransitions = { ...prev }
      const key = `${sceneId}-${position}`

      if (transitionType === null) {
        delete newTransitions[key]
      } else {
        newTransitions[key] = transitionType
      }
      return newTransitions
    })

    if (transitionType === null) {
      message.success('Transition removed')
    } else {
      message.success(`Transition updated to ${transitionType}`)
    }
  }

  // ===== UPLOAD HANDLERS =====
  const handleUploadVideo = (sceneId, shotId, file) => {
    if (!file) return
    if (!file.type.includes('video/')) {
      message.error('Please upload a video file')
      return
    }

    const videoUrl = URL.createObjectURL(file)
    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId ? { ...shot, vidSrc: videoUrl } : shot
            )
          }
        }
        return scene
      })
    )

    // Invalidate scene preview when a video is uploaded
    invalidateScenePreview(sceneId)
    message.success('Video uploaded successfully!')
  }

  const handleUploadImage = file => {
    if (!file) return false
    if (!file.type.includes('image/')) {
      message.error('Please upload an image file')
      return false
    }

    const imageUrl = URL.createObjectURL(file)
    updateAdvancedSettings('imageSrc', imageUrl)
    message.success('Image uploaded successfully!')
    return false
  }

  const handleSortEnd = (sceneId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    setScenes(prev =>
      prev.map(scene =>
        scene.id === sceneId
          ? { ...scene, shots: arrayMove(scene.shots, oldIndex, newIndex) }
          : scene
      )
    )
    // Invalidate scene preview when shots are reordered
    invalidateScenePreview(sceneId)
  }

  const handleAllRegeneration = () => {
    Modal.confirm({
      title: 'Do you want to generate all videos?',
      content: 'This will overwrite all the current videos.',
      centered: true,
      onOk() {
        handleGenerateFullVideo()
      }
    })
  }

  // ===== EFFECTS =====
  useEffect(() => {
    const initializeDurationCalculation = async () => {
      // Start calculation if not already in progress
      if (!isCalculatingDurations) {
        try {
          const payload = constructAssemblyPayload(scenes)
          // Exclude location_map from shooting_list as per requirement
          const {
            location_map,
            ...shootingListWithoutLocationMap
          } = payload.clustered_shooting_list

          const output = await callCalculateDurations(
            {
              shooting_list: shootingListWithoutLocationMap
            },
            formatDurationOutput,
            scenes
          )
          // If output returned, it means polling finished successfully
          if (output) {
            setFormData(prev => ({ ...prev, shotList: output }))
          }
        } catch (error) {
          console.error('Duration calculation failed:', error)
        }
      }
    }

    initializeDurationCalculation()
  }, [])

  // Track the last shotList state to detect when new shots are added (not just updates)
  const lastShotListRef = useRef(null)
  const hasCheckedInitialVideosRef = useRef(false)

  useEffect(() => {
    if (!videoProvider && formData.durationTaskId) {
      return
    }

    // Only check for auto-generation when:
    // 1. Provider changes (reset check flag)
    // 2. New shots are added (shotList length or structure changes significantly)
    const currentShotList = formData?.shotList || []
    const lastShotList = lastShotListRef.current

    // Check if this is a new shotList (initial load or new shots added)
    const isNewShotList =
      !lastShotList ||
      lastShotList.length !== currentShotList.length ||
      currentShotList.some((scene, idx) => {
        const lastScene = lastShotList[idx]
        return (
          !lastScene ||
          lastScene.id !== scene.id ||
          (scene.shots || []).length !== (lastScene.shots || []).length
        )
      })

    // Reset check flag if provider changed or shotList structure changed significantly
    if (!videoProvider || isNewShotList) {
      hasCheckedInitialVideosRef.current = false
    }

    // Update the ref
    lastShotListRef.current = currentShotList

    if (
      currentShotList.length > 0 &&
      videoProvider &&
      !generatingVideoForAllScenes &&
      !autoRegenerationTriggeredRef.current &&
      !hasCheckedInitialVideosRef.current
    ) {
      hasCheckedInitialVideosRef.current = true
      const mismatchedScenes = []
      const shotsToProcess = currentShotList
        .map(scene => {
          const invalidShots = (scene.shots || []).filter(shot => {
            // 1. Is it already generating?
            const isCurrentlyGenerating = (videoGenerating || []).some(
              v => v.sceneId === scene.id && v.shotId === shot.id
            )
            if (isCurrentlyGenerating) return false

            // 2. Skip shots that have errors - let user manually retry them
            if (shot.errorGeneratingVideo) return false

            const hasVideo = !!shot.vidSrc
            const currentDuration = parseFloat(shot.video_duration || 0)
            const generatedDuration =
              shot.generated_video_duration !== null &&
              shot.generated_video_duration !== undefined
                ? parseFloat(shot.generated_video_duration)
                : null

            // 3. Not generated yet - but only auto-generate if it's not a failed attempt
            if (!hasVideo && !shot.errorGeneratingVideo) {
              return true
            }

            // 4. Duration mismatch - auto-regenerate for duration sync issues
            if (generatedDuration !== null && currentDuration > 0) {
              const mismatch =
                Math.abs(currentDuration - generatedDuration) > 0.1
              if (mismatch) {
                console.log(
                  `Regeneration Triggered for ${shot.id}: Current=${currentDuration}, Previously Generated=${generatedDuration}`
                )
                if (!mismatchedScenes.includes(scene.title || scene.id)) {
                  mismatchedScenes.push(scene.title || scene.id)
                }
              }
              return mismatch
            }

            // 5. Missing tracking for a shot with video - we should probably trigger once to be safe
            if (hasVideo && generatedDuration === null) {
              if (!mismatchedScenes.includes(scene.title || scene.id)) {
                mismatchedScenes.push(scene.title || scene.id)
              }
              return true
            }

            return false
          })

          if (invalidShots.length > 0) {
            return {
              ...scene,
              shots: invalidShots
            }
          }
          return null
        })
        .filter(s => s !== null)

      if (shotsToProcess.length > 0) {
        autoRegenerationTriggeredRef.current = true
        if (mismatchedScenes.length > 0) {
          setAffectedScenesByMismatch(mismatchedScenes)
          setShowRegenerationInfo(true)
        }
        handleGenerateFullVideo(shotsToProcess)
      }
    }

    // Reset the trigger ref when everything is synced and not generating anymore
    if (autoRegenerationTriggeredRef.current && !generatingVideoForAllScenes) {
      const hasMismatches = currentShotList.some(scene =>
        (scene.shots || []).some(shot => {
          const currentDuration = parseFloat(shot.video_duration || 0)
          const generatedDuration = shot.generated_video_duration
            ? parseFloat(shot.generated_video_duration)
            : null
          return (
            shot.vidSrc &&
            generatedDuration &&
            Math.abs(currentDuration - generatedDuration) > 0.1
          )
        })
      )
      if (!hasMismatches) {
        autoRegenerationTriggeredRef.current = false
      }
    }
  }, [videoProvider, videoGenerating, generatingVideoForAllScenes])

  // Sync cache to formData for persistence
  useEffect(() => {
    if (Object.keys(scenePreviewCache).length > 0) {
      setFormData(prev => ({
        ...prev,
        scenePreviewCache
      }))
    }
  }, [scenePreviewCache])

  // Initialize cache from formData on mount
  useEffect(() => {
    if (formData?.scenePreviewCache) {
      setScenePreviewCache(formData.scenePreviewCache)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    openVideoEditor: () => {
      setShowVideoEditor(true)
    }
  }))

  // ===== COMPUTED VALUES =====
  const { totalVideosGenerated, totalVideosToGenerate } = useMemo(() => {
    const totalVideosGenerated = scenes.reduce((count, scene) => {
      return (
        count +
        scene.shots.filter(
          shot =>
            shot.vidSrc &&
            !videoGenerating.some(
              v => v.sceneId === scene.id && v.shotId === shot.id
            )
        ).length
      )
    }, 0)
    const totalVideosToGenerate = scenes.reduce((count, scene) => {
      return count + scene.shots.length
    }, 0)
    return { totalVideosGenerated, totalVideosToGenerate }
  }, [scenes, videoGenerating])

  const isAllScenesGenerated = scenes.every(scene =>
    scene.shots.every(shot => shot.vidSrc)
  )

  const hasFailedVideos = useMemo(() => {
    return scenes.some(scene =>
      scene.shots.some(
        shot =>
          shot.errorGeneratingVideo ||
          (!shot.vidSrc &&
            !videoGenerating.some(
              v => v.sceneId === scene.id && v.shotId === shot.id
            ))
      )
    )
  }, [scenes, videoGenerating])

  const isAnyVideoGenerating =
    videoGenerating.length > 0 || generatingVideoForAllScenes

  // ===== RENDER MODALS =====

  // Scene Preview Status (Loading/Error)
  if (showScenePreview && (scenePreviewError || !previewSceneVideo)) {
    return (
      <GenerationStatusModal
        visible={showScenePreview}
        status={scenePreviewError ? 'error' : 'loading'}
        title={scenePreviewError ? 'Preview Failed' : 'Generating Preview...'}
        subTitle={
          !scenePreviewError &&
          'We are assembling your scene shots. Please wait. This may take a few minutes.'
        }
        error={scenePreviewError}
        small
        onClose={() => {
          if (scenePreviewError) {
            setShowScenePreview(false)
            setScenePreviewError(null)
          } else {
            Modal.confirm({
              title: 'Generation in progress',
              content:
                'Video generation is still running in the background. Are you sure you want to close this status window?',
              okText: 'Yes, Close',
              cancelText: 'Stay',
              centered: true,
              onOk: () => {
                setShowScenePreview(false)
                setScenePreviewError(null)
              }
            })
          }
        }}
        onRetry={() => {
          const lastSceneId = Object.keys(scenePreviewCache).find(id => {
            return false
          })
        }}
      />
    )
  }

  // Scene Preview Video Modal
  if (showScenePreview && previewSceneVideo) {
    return (
      <AIVideoModal
        visible={showScenePreview}
        videoUrl={previewSceneVideo}
        title="Scene Preview"
        onClose={() => {
          setShowScenePreview(false)
          setPreviewSceneVideo(null)
          setScenePreviewError(null)
        }}
      />
    )
  }

  // Final Video Status (Error)
  if (showMainVideo && (finalVideoError || !formData?.finalVideo)) {
    return (
      <GenerationStatusModal
        visible={showMainVideo}
        small
        status="error"
        title="Generation Failed"
        subTitle="Something went wrong while creating your final video."
        error={finalVideoError}
        onClose={() => setShowMainVideo(false)}
        onRetry={() => {
          setShowMainVideo(false)
          setShowVideoEditor(true)
        }}
      />
    )
  }

  // Final Video Modal
  if (showMainVideo && formData.finalVideo) {
    return (
      <AIVideoModal
        visible={showMainVideo}
        videoUrl={formData.finalVideo}
        title="Your AI Generated Video"
        onClose={() => setShowMainVideo(false)}
      />
    )
  }

  // Generating Final Video Modal (Loading)
  if (generateMainVideo) {
    return (
      <GenerationStatusModal
        visible={generateMainVideo}
        status="loading"
        small
        title="Generating Final Video"
        subTitle="Please wait while we process your request. This may take a few minutes."
        onClose={() => {
          Modal.confirm({
            title: 'Generation in progress',
            content:
              'Final video generation is still running in the background. Are you sure you want to close this status window?',
            okText: 'Yes, Close',
            cancelText: 'Stay',
            centered: true,
            onOk: () => {
              setGenerateMainVideo(false)
            }
          })
        }}
      />
    )
  }

  // ===== MAIN RENDER =====
  if (isCalculatingDurations) {
    return (
      <div className="video-generation-container">
        <StatusView
          status="loading"
          title="Calculating Durations..."
          subTitle="We're calculating scene durations and finalizing your video structure. This ensures everything is perfectly timed before you start generating."
          progress={calculationProgress}
          inline
        />
      </div>
    )
  }

  return (
    <div className="video-generation-container">
      <div className="header">
        <StepTitle
          iconType="video-camera"
          title="Video Generation"
          description="Create videos for your project"
          isLoading={isAnyVideoGenerating}
          progress={{
            current: totalVideosGenerated,
            total: totalVideosToGenerate
          }}
        />

        {hasFailedVideos && (
          <Button
            style={{ margin: '0 8px' }}
            icon="reload"
            type="danger"
            ghost
            onClick={handleRetryFailedVideos}
            disabled={generatingVideoForAllScenes}
          >
            Retry Failed
          </Button>
        )}
        {isAllScenesGenerated && (
          <Button
            style={{ margin: '0 8px' }}
            icon="play-circle"
            loading={generatingVideoForAllScenes}
            onClick={handleAllRegeneration}
          >
            Re-Generate All Videos
          </Button>
        )}

        {formData.finalVideo && (
          <Button
            style={{ margin: '0 8px' }}
            icon="eye"
            type="green"
            onClick={() => setShowMainVideo(true)}
          >
            Watch Generated Video
          </Button>
        )}
      </div>

      {scenes.map((scene, index) => (
        <Fragment key={scene.id + 'scene' + index}>
          <Scene
            key={scene.id}
            scene={scene}
            transitions={transitions}
            formData={formData}
            isGeneratingAll={generatingVideoForAllScenes}
            videoGenerating={videoGenerating}
            setScenes={setScenes}
            handleRegenerateVideo={handleRegenerateVideo}
            handleUploadVideo={handleUploadVideo}
            handleEditSettings={handleEditSettings}
            handleTransitionSelect={handleTransitionSelect}
            videoProvider={videoProvider}
            onSortEnd={params => handleSortEnd(scene.id, params)}
            onPreviewScene={handlePreviewScene}
            updateShotDataRef={updateShotDataRef}
          />
        </Fragment>
      ))}

      <VideoEditorModal
        isSingleScene={editorScenes?.length === 1}
        visible={showVideoEditor}
        scenes={editorScenes?.length > 0 ? editorScenes : scenes}
        transitions={
          editorTransitions && Object.keys(editorTransitions).length > 0
            ? editorTransitions
            : transitions
        }
        onConfirm={handleGenerateFinalVideo}
        onCancel={() => {
          setShowVideoEditor(false)
          setEditorScenes([])
          setEditorTransitions({})
        }}
        finalVideoExists={formData?.finalVideo}
      />

      {/* Settings Drawer */}
      <Drawer
        title="Clip Editor"
        placement="right"
        closable={true}
        onClose={() => {
          setEditingSettings(null)
          setDrawerVisible(false)
        }}
        visible={drawerVisible}
        width={400}
      >
        {editingSettings &&
          (() => {
            const scene = scenes.find(s => s.id === editingSettings.sceneId)
            const shot = scene?.shots.find(
              sh => sh.id === editingSettings.shotId
            )
            if (!shot) return null
            const settings = {
              ...shot,
              sceneId: editingSettings.sceneId,
              shotId: editingSettings.shotId
            }
            return (
              <SettingsContent
                settings={settings}
                updateSettings={updateSettings}
                handleAdvancedSettings={handleAdvancedSettings}
                saveSettings={saveSettings}
              />
            )
          })()}
      </Drawer>

      {/* Advanced Settings Drawer */}
      <Drawer
        title="Advanced Shot Settings"
        placement="right"
        closable={true}
        onClose={() => setAdvancedSettingsVisible(false)}
        visible={advancedSettingsVisible}
        width={520}
        zIndex={1001}
      >
        {advancedSettings && (
          <AdvancedSettingsContent
            settings={advancedSettings}
            updateSettings={updateAdvancedSettings}
            saveSettings={saveAdvancedSettings}
            handleUploadImage={handleUploadImage}
            formData={formData}
            onCancel={() => setAdvancedSettingsVisible(false)}
          />
        )}
      </Drawer>

      <RegenerationInfoModal
        visible={showRegenerationInfo}
        onClose={() => setShowRegenerationInfo(false)}
        affectedScenes={affectedScenesByMismatch}
      />
    </div>
  )
})

export default VideoGeneration
